import { useId, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import { getParameterDefinition } from "../../utils/parameterCatalog";

interface ParameterHelpProps {
  parameterKey?: string;
  label?: string;
  unit?: string;
}

const fallbackLabel = (parameterKey?: string) => {
  if (!parameterKey) return "Parámetro";
  return parameterKey.charAt(0).toUpperCase() + parameterKey.slice(1);
};

export default function ParameterHelp({ parameterKey, label, unit }: ParameterHelpProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const definition = getParameterDefinition(parameterKey);
  const visibleLabel = label || definition?.label || fallbackLabel(parameterKey);
  const visibleUnit = unit || definition?.unit;
  const titleId = useId();
  const descriptionId = useId();
  const popoverId = useId();
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        type="button"
        size="small"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={`Ayuda sobre ${visibleLabel}`}
        aria-expanded={open ? "true" : undefined}
        aria-controls={open ? popoverId : undefined}
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          color: "text.secondary",
        }}
      >
        <HelpOutlineRoundedIcon fontSize="small" aria-hidden="true" />
      </IconButton>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          role: "dialog",
          "aria-labelledby": titleId,
          "aria-describedby": descriptionId,
          sx: { maxWidth: 360, width: "calc(100vw - 32px)" },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
              <Typography id={titleId} variant="subtitle1" fontWeight={800}>
                {visibleLabel}
              </Typography>
              <IconButton
                type="button"
                size="small"
                aria-label={`Cerrar ayuda sobre ${visibleLabel}`}
                onClick={() => setAnchorEl(null)}
                sx={{ mt: -0.75, mr: -0.75 }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Typography id={descriptionId} variant="body2" color="text.secondary">
              {definition?.description || "No hay una definición disponible para este parámetro."}
            </Typography>
            {visibleUnit && (
              <Typography variant="caption" color="text.secondary">
                Unidad: {visibleUnit}
              </Typography>
            )}
            {definition?.source && (
              <Typography variant="caption" color="text.secondary">
                Fuente de la definición: {definition.source}
              </Typography>
            )}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
